import { Component, OnInit } from "@angular/core";
import { ServiceMetaData } from "../shared/models/service-meta.model";
import { ofType, Actions } from "@ngrx/effects";
import { map, take, takeUntil } from "rxjs/operators";
import { BroshService } from "../shared/services/brosh.service";
import {
  SupplyOrder,
  SupplyOrderSearchParams,
} from "../shared/models/supply-order.model";
import { AuthService } from "../shared/services/auth.service";
import { DataElement, PaginationParams } from "../shared/models/common";
import { Subject, Subscription } from "rxjs";
import { ActivatedRoute, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import {
  supplyOrderSearchMetadata,
  supplyOrders,
  supplySelectedClinic,
} from "../shared/ngrx/ngrx-selectors";
import {
  APPROVE_SUPPLY_ORDER,
  CREATE_SUPPLY_ORDER,
  REQUEST_DASHBOARD_REFRESH,
  SUPPLIES_SELECT_CLINIC,
} from "../shared/ngrx/ngrx-actions";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { CommonModule } from "@angular/common";
import { TooltipDirective } from "../shared/components/ui/tooltip.directive";
import { TableComponent } from "../shared/components/ui/table/table.component";
import { LoaderComponent } from "../shared/components/ui/loader/loader.component";
import { CreateSuppliesOrderComponent } from "./create-supplies-order/create-supplies-order.component";
import { ModalPopupComponent } from "../shared/components/ui/modal-popup.component";
import { InputWrapComponent } from "../shared/components/ui/input-wrap.component";
import { FormsModule } from "@angular/forms";

@Component({
  selector: "app-supplies",
  templateUrl: "./supplies.component.html",
  styleUrls: ["./supplies.component.scss"],
  standalone: true,
  imports: [
    TranslateModule,
    CommonModule,
    TooltipDirective,
    TableComponent,
    LoaderComponent,
    CreateSuppliesOrderComponent,
    ModalPopupComponent,
    InputWrapComponent,
    FormsModule,
  ],
})
export class SuppliesComponent implements OnInit {
  ordersLoading: boolean;
  selectedOrder: SupplyOrder;
  allowedApprove: boolean = false;
  numberOfRows: number = 12;
  rangeFrom: number = 1;
  rangeTo: number = this.numberOfRows;
  totalRecords: number;
  searchParams: SupplyOrderSearchParams;
  destroy$ = new Subject();
  sub: Subscription;
  sub2: Subscription;
  selectDeliverySw: boolean;
  displayColumnsList: string[];
  displayActionTemplate: boolean = false;
  labUserSw: boolean;
  displayPublicBatchScan: boolean;
  publicBatchId: string;

  get orderSearchMetaData(): ServiceMetaData {
    return this.store.selectSignal(supplyOrderSearchMetadata)() || {};
  }

  get supplyOrders(): SupplyOrder[] {
    return this.store.selectSignal(supplyOrders)();
  }

  get selectedClinic(): DataElement {
    return this.store.selectSignal(supplySelectedClinic)();
  }

  get selectedClinicTitle(): string {
    if (this.selectedClinic) {
      return " - " + this.selectedClinic.text;
    } else return "";
  }

  constructor(
    private readonly store: Store,
    private broshService: BroshService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private actions$: Actions,
    private translateService: TranslateService
  ) {
    this.allowedApprove =
      this.authService.currentUser.authDetails.getBooleanValue(
        "DELIERY.APPROVE_DELIVERY_SW"
      );

    this.labUserSw = this.authService.currentUser.org == "LAB";

    if (this.labUserSw) {
      this.displayActionTemplate = true;
      this.displayColumnsList = [
        "createDateFormatted",
        "clinicDescr",
        "city",
        "itemListFormatted",
        "notes",
        "statusDescr",
        //"createUserName",
        //"approveUserName"
      ];
    } else if (this.authService.currentUser.isMultiClinicUser) {
      this.displayColumnsList = [
        "createDateFormatted",
        "clinicDescr",
        "itemListFormatted",
        "notes",
        "statusDescr",
      ];
    } else {
      this.displayColumnsList = [
        "createDateFormatted",
        "itemListFormatted",
        "notes",
        "statusDescr",
      ];
    }

    this.actions$
      .pipe(
        ofType(APPROVE_SUPPLY_ORDER),
        takeUntil(this.destroy$),
        map(({}) => {
          this.runOrdersSearch(false);
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(CREATE_SUPPLY_ORDER),
        takeUntil(this.destroy$),
        map(({}) => {
          this.runOrdersSearch(true);
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(SUPPLIES_SELECT_CLINIC),
        takeUntil(this.destroy$),
        map(({}) => {
          this.resetSearchParams();

          this.searchParams.clinicId =
            this.store.selectSignal(supplySelectedClinic)()?.id;

          this.runOrdersSearch(false);
        })
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.sub = this.route.queryParams.subscribe((params) => {
      this.resetSearchParams();
      if (params && params["clinicId"]) {
        this.searchParams.clinicId = params["clinicId"];
      }
      if (params && params["status"]) {
        this.searchParams.status = params["status"];
        this.searchParams.type = params["type"];
      }

      this.runOrdersSearch(false);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(1);
    if (this.sub) this.sub.unsubscribe();
    if (this.sub2) this.sub2.unsubscribe();
  }

  resetSearchParams() {
    this.searchParams = {
      rangeTo: this.rangeTo,
      rangeFrom: this.rangeFrom,
      searchId: null,
    };
  }

  runOrdersSearch(newSearch: boolean) {
    if (newSearch) {
      this.resetSearchParams();
    }

    this.ordersLoading = true;
    this.broshService.getSupplyOrders(this.searchParams).subscribe(
      () => {
        this.ordersLoading = false;
      },
      () => (this.ordersLoading = false)
    );
  }

  onApproveClicked(order: SupplyOrder) {
    this.publicBatchId = null;
    if (order.isBarcodeRollOrder) {
      this.selectedOrder = order;
      this.displayPublicBatchScan = true;
    } else {
      this.approveOrder(order);
    }
  }

  approveOrder(order: SupplyOrder) {
    this.displayPublicBatchScan = false;
    order.approveOrderLoading = true;
    this.broshService
      .approveSupplyOrder(order.orderId, this.publicBatchId)
      .subscribe(
        () => {
          if (order.isDeliveryOrder) {
            this.broshService.mailSuppliesOrders(order.orderId).subscribe(
              (data) => {
                this.afterOrderApproved(order);
              },
              (err) => {
                this.afterOrderApproved(order);
              }
            );
          } else {
            this.afterOrderApproved(order);
          }
        },
        (err) => {
          order.approveOrderLoading = false;
        }
      );
  }

  afterOrderApproved(order: SupplyOrder) {
    order.approveOrderLoading = false;
    this.store.dispatch(APPROVE_SUPPLY_ORDER());

    this.store.dispatch(REQUEST_DASHBOARD_REFRESH());
  }

  loadDataOnPagination(event: PaginationParams) {
    if (
      !this.supplyOrders ||
      (event.rangeFrom == this.searchParams.rangeFrom &&
        event.rangeTo == this.searchParams.rangeTo)
    )
      return;

    this.searchParams.rangeFrom = event.rangeFrom;
    this.searchParams.rangeTo = event.rangeTo;
    this.searchParams.searchId = this.orderSearchMetaData?.searchId;

    this.runOrdersSearch(false);
  }
}
